import React, { ReactNode, useMemo, MouseEvent } from 'react';
import styled from '@emotion/styled';
import { defaultTheme } from '../../config/theming';

const Button = styled.button<{ loading: number }>`
  min-width: 130px;
  width: auto;
  background-color: ${defaultTheme.colors.primary};
  color: #fff;
  padding: 8px 12px;
  display: flex;
  justify-content: center;
  align-items: center;
  box-shadow: 0px 3px 7px rgba(0, 0, 0, 0.25);
  cursor: ${p => (p.loading ? 'not-allowed' : 'pointer')};
  border-radius: 3px;
  border: 1px solid;
  border-color: ${defaultTheme.colors.primary};
  &:hover {
    background-color: #57a8e6;
    border-color: #57a8e6;
  }
  &:focus {
    background-color: ${defaultTheme.colors.primary};
    border-color: #57a8e6;
    box-shadow: 0px 2px 7px ${defaultTheme.colors.primary};
  }
  &:active {
    background-color: #006aa1;
    border-color: #707070;
    box-shadow: none;
  }
  &:disabled {
    background-color: #e4e4e4;
    color: #aaaaaa;
    border-color: #e4e4e4;
    box-shadow: none;
  }
`;

const Loading = styled.span`
  display: inline-block;
  width: 25px;
  height: 25px;
  border: 3px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: #fff;
  animation: rotating 1s linear infinite;

  @keyframes rotating {
    from {
      -ms-transform: rotate(0deg);
      -moz-transform: rotate(0deg);
      -webkit-transform: rotate(0deg);
      -o-transform: rotate(0deg);
      transform: rotate(0deg);
    }
    to {
      -ms-transform: rotate(360deg);
      -moz-transform: rotate(360deg);
      -webkit-transform: rotate(360deg);
      -o-transform: rotate(360deg);
      transform: rotate(360deg);
    }
  }
`;

const LoadingTextWrapper = styled.span`
  margin-left: 0.5em;
`;
interface Props {
  disabled?: boolean;
  clickFn: (e?: MouseEvent<HTMLButtonElement>) => void;
  children: ReactNode;
  loading?: boolean;
  loadingText?: string;
  testId?: string;
}

export const PrimaryButton = (props: Props) => {
  const innerText = useMemo(() => {
    if (props.loading) {
      if (props.loadingText) {
        return (
          <>
            <Loading data-testid="button-spinner" />
            <LoadingTextWrapper>{props.loadingText}</LoadingTextWrapper>
          </>
        );
      }
      return <Loading data-testid="button-spinner" />;
    }
    return props.children;
  }, [props.children, props.loading, props.loadingText]);

  return (
    <Button
      data-testid={props.testId}
      disabled={props.disabled}
      loading={props.loading ? 1 : 0}
      onClick={e => {
        if (!props.loading) props.clickFn(e);
      }}
    >
      {innerText}
    </Button>
  );
};
